import { paintIndicators } from "#canvas/renderers.ts";
import { usesBrushSize } from "#canvas/types.ts";
import type { Cell, Color, Layer, PixelCanvasRef, PixelOp, ToolMode } from "#canvas/types.ts";
import { useLayerRenderers } from "#canvas/useLayerRenderers.ts";
import { useLiveLayers } from "#canvas/useLiveLayers.ts";
import { usePointerTools } from "#canvas/usePointerTools.ts";
import { useViewport } from "#canvas/useViewport.ts";
import { Application, extend } from "@pixi/react";
import { Container, Graphics, Sprite } from "pixi.js";
import {
  type PointerEvent as ReactPointerEvent,
  type Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  type WheelEvent as ReactWheelEvent,
} from "react";

extend({ Container, Sprite, Graphics });

interface Props {
  rows: number;
  cols: number;
  layers: Layer[]; // controlled
  activeLayerId: string;
  color: Color;
  mode: ToolMode;
  brushSize: number;
  shapeFill?: boolean;
  indicators?: PixelOp[];
  bg?: Color;
  gridStroke?: Color;
  ref?: Ref<PixelCanvasRef>;
  onCommit: (layers: Layer[]) => void;
  onPick?: (color: Color) => void;
  /** Wheel-driven brush-size adjustment. Direction: +1 = bigger, -1 = smaller. */
  onBrushBump?: (direction: 1 | -1) => void;
}

// Trackpads emit many small wheel events per gesture. Cooldown collapses a
// burst into a single step; mouse-wheel detents are slower than the cooldown
// so each detent still registers immediately.
const BRUSH_BUMP_COOLDOWN_MS = 120;

export function PixelCanvas(props: Props) {
  const {
    rows,
    cols,
    layers,
    activeLayerId,
    color,
    mode,
    brushSize,
    shapeFill = false,
    indicators,
    bg = "#1f2125",
    gridStroke = "#2a2d31",
    ref,
    onCommit,
    onPick,
    onBrushBump,
  } = props;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const { viewport, panDelta, setPanDelta, bumpZoom } = useViewport(wrapRef, cols, rows);

  const { renderers, indicatorRender, previewRender } = useLayerRenderers(layers, cols, rows, indicators);
  const { getLive, applyLiveOps, commit, discardLive, setAll, undo, redo } = useLiveLayers({
    layers,
    activeLayerId,
    renderers,
    onCommit,
  });

  // ─── coordinate helpers ─────────────────────────────────────────────────────
  const eventToCell = useCallback((e: ReactPointerEvent<HTMLDivElement>): Cell | null => {
    const el = wrapRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left - viewport.pan.x;
    const y = e.clientY - rect.top - viewport.pan.y;
    if (viewport.zoom <= 0) return null;
    const col = Math.floor(x / viewport.zoom);
    const row = Math.floor(y / viewport.zoom);
    if (row < 0 || row >= rows || col < 0 || col >= cols) return null;
    return { row, col };
  }, [viewport, rows, cols]);

  // ─── pointer flow ───────────────────────────────────────────────────────────
  const paintPreview = useCallback((pixels: PixelOp[]): void => {
    if (previewRender) paintIndicators(previewRender, pixels);
  }, [previewRender]);

  const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } = usePointerTools({
    applyLiveOps,
    commit,
    discardLive,
    getLive,
    paintPreview,
    setPanDelta,
    eventToCell,
    mode,
    color,
    brushSize,
    shapeFill,
    panDelta,
    activeLayerId,
  });

  const onClick = useCallback((e: ReactPointerEvent<HTMLDivElement>): void => {
    if (mode.kind !== "picker" || !onPick) return;
    const cell = eventToCell(e);
    if (!cell) return;
    const live = getLive();
    for (let i = live.length - 1; i >= 0; i--) {
      const layer = live[i];
      if (!layer?.visible) continue;
      const px = layer.data[cell.row]?.[cell.col];
      if (px) {
        onPick(px);
        return;
      }
    }
  }, [mode, onPick, eventToCell, getLive]);

  const lastBrushBumpRef = useRef(0);

  const onWheel = useCallback((e: ReactWheelEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const direction = e.deltaY < 0 ? 1 : -1;
    // Modifier always zooms — escape hatch when brush tools own the wheel.
    if (e.ctrlKey || e.metaKey) {
      bumpZoom(direction);
      return;
    }
    if (onBrushBump && usesBrushSize(mode, shapeFill)) {
      const now = performance.now();
      if (now - lastBrushBumpRef.current < BRUSH_BUMP_COOLDOWN_MS) return;
      lastBrushBumpRef.current = now;
      onBrushBump(direction);
      return;
    }
    bumpZoom(direction);
  }, [bumpZoom, mode, shapeFill, onBrushBump]);

  // ─── imperative API ─────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    setLayers: setAll,
    getLayers: getLive,
    setIndicators: (pixels) => {
      if (indicatorRender) paintIndicators(indicatorRender, pixels);
    },
    undo,
    redo,
    exportPng: () => {
      const out = document.createElement("canvas");
      out.width = cols;
      out.height = rows;
      const ctx = out.getContext("2d");
      if (!ctx) return "";
      ctx.imageSmoothingEnabled = false;
      for (const layer of getLive()) {
        if (!layer.visible) continue;
        const r = renderers.get(layer.id);
        if (!r) continue;
        ctx.globalAlpha = layer.opacity ?? 1;
        ctx.drawImage(r.canvas, 0, 0);
      }
      ctx.globalAlpha = 1;
      return out.toDataURL("image/png");
    },
  }), [cols, rows, renderers, indicatorRender, setAll, getLive, undo, redo]);

  // ─── render ─────────────────────────────────────────────────────────────────
  const visibleLayers = useMemo(() => layers.filter((l) => l.visible), [layers]);

  const drawGrid = useCallback((g: Graphics) => {
    g.clear();
    g.rect(0, 0, cols, rows).fill({ color: bg });
    g.setStrokeStyle({ width: 1 / Math.max(1, viewport.zoom), color: gridStroke, alpha: 0.5 });
    for (let r = 0; r <= rows; r++) {
      g.moveTo(0, r);
      g.lineTo(cols, r);
    }
    for (let c = 0; c <= cols; c++) {
      g.moveTo(c, 0);
      g.lineTo(c, rows);
    }
    g.stroke();
  }, [cols, rows, bg, gridStroke, viewport.zoom]);

  const ready = viewport.w > 0 && viewport.h > 0;

  return (
    <div
      ref={wrapRef}
      data-testid="pixel-canvas"
      className="relative w-full h-full overflow-hidden touch-none select-none cursor-crosshair"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onClick={onClick}
      onWheel={onWheel}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
    >
      {ready
        ? (
          <Application
            resizeTo={wrapRef}
            backgroundColor={bg}
            antialias={false}
            roundPixels
          >
            <pixiContainer x={viewport.pan.x} y={viewport.pan.y} scale={viewport.zoom}>
              <pixiGraphics draw={drawGrid} />
              {visibleLayers.map((layer) => {
                const r = renderers.get(layer.id);
                if (!r) return null;
                return (
                  <pixiSprite
                    key={layer.id}
                    texture={r.texture}
                    alpha={layer.opacity ?? 1}
                  />
                );
              })}
              {indicatorRender ? <pixiSprite texture={indicatorRender.texture} alpha={1} /> : null}
              {previewRender ? <pixiSprite texture={previewRender.texture} alpha={0.8} /> : null}
            </pixiContainer>
          </Application>
        )
        : null}
    </div>
  );
}
